import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { usageTone } from '@/lib/usage-utils';

interface UsageGaugeProps {
  value: number;
  usedLabel?: string;
  totalLabel?: string;
  size?: number;
  strokeWidth?: number;
  className?: string;
}

export function UsageGauge({
  value,
  usedLabel,
  totalLabel,
  size = 168,
  strokeWidth = 14,
  className,
}: UsageGaugeProps) {
  const clamped = Math.max(0, Math.min(100, value));
  const tone = usageTone(clamped);

  const [progress, setProgress] = useState(0);
  useEffect(() => {
    const raf = requestAnimationFrame(() => setProgress(clamped));
    return () => cancelAnimationFrame(raf);
  }, [clamped]);

  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const offset = circumference * (1 - progress / 100);

  return (
    <div
      className={cn('relative inline-flex items-center justify-center', className)}
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} className="-rotate-90">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="hsl(var(--muted))"
          strokeWidth={strokeWidth}
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={tone.color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={offset}
          style={{
            transition:
              'stroke-dashoffset 900ms cubic-bezier(0.22, 1, 0.36, 1), stroke 300ms ease',
          }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={cn('text-3xl font-black leading-none', tone.text)}>{clamped}%</span>
        {usedLabel && <span className="mt-1 text-sm font-bold">{usedLabel}</span>}
        {totalLabel && <span className="text-xs text-muted-foreground">de {totalLabel}</span>}
      </div>
    </div>
  );
}
